import React, { useState, useEffect } from 'react';
import * as api from '../services/api';
import Icons from './Icons';
import { IMG_MODELS, VID_MODELS } from '../constants/models';

export default function TemplateSlideWindow({ isOpen, onClose, modelId, onSelect, mode = 'image' }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    const cached = api.getCachedTemplates();
    if (cached.length > 0) {
      setLoading(false);
    } else {
      setLoading(true);
    }
    api.fetchTemplates()
      .then(data => {
        if (!cancelled) {
          const models = mode === 'video' ? VID_MODELS : IMG_MODELS;
          const currentModelInfo = models.find(m => m.id === modelId);
          const currentModelLabel = currentModelInfo?.label;

          const filtered = data.filter(t => {
            const isVideo = t.type === 'video' || t.editType === 'text_to_video' || t.editType === 'frame-to-video';
            if (mode === 'video' && !isVideo) return false;
            if (mode === 'image' && isVideo) return false;
            
            if (!modelId) return true;
            
            const cleanTmplModel = String(t.model || '').trim().toLowerCase();
            const cleanCurrentId = String(modelId || '').trim().toLowerCase();
            const cleanCurrentLabel = String(currentModelLabel || '').trim().toLowerCase();
            
            return cleanTmplModel === cleanCurrentId || 
                   cleanTmplModel === cleanCurrentLabel ||
                   cleanTmplModel.replace(/\s+/g, '-') === cleanCurrentId ||
                   cleanTmplModel === cleanCurrentId.replace(/-/g, ' ');
          });
          setTemplates(filtered);
        }
      })
      .catch(console.error)
      .finally(() => { if (!cancelled) setLoading(false); });
      
    return () => { cancelled = true; };
  }, [isOpen, modelId, mode]);

  if (!isOpen) return null;

  return (
    <>
      <style>{`
        @keyframes popupInTmpl {
          from { opacity: 0; transform: translateY(10px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .tmpl-slide-drawer {
          position: fixed;
          bottom: 24px; left: 416px;
          width: 340px;
          max-height: calc(100vh - 48px);
          background: var(--s1);
          border: 1px solid var(--b2);
          border-radius: 16px;
          z-index: 60;
          display: flex;
          flex-direction: column;
          animation: popupInTmpl 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          box-shadow: 0 20px 50px rgba(0,0,0,0.5);
          transform-origin: bottom left;
        }
        @media (max-width: 768px) {
          .tmpl-slide-drawer {
            left: 10px; right: 10px; bottom: 10px;
            width: auto;
          }
        }
        .tmpl-slide-overlay {
          position: fixed;
          inset: 0;
          background: transparent;
          z-index: 59;
        }
        .tmpl-card {
          position: relative;
          border-radius: 10px;
          overflow: hidden;
          background: var(--s2);
          border: 1px solid var(--b1);
          cursor: pointer;
          aspect-ratio: 1;
          transition: transform 0.2s, border-color 0.2s;
        }
        .tmpl-card:hover {
          transform: scale(1.02);
          border-color: var(--ac);
        }
        .tmpl-card img {
          width: 100%; height: 100%;
          object-fit: cover;
        }
        .tmpl-card-ov {
          position: absolute; inset: 0;
          background: linear-gradient(to top, rgba(0,0,0,0.8), transparent 50%);
          display: flex; flex-direction: column; justify-content: flex-end;
          padding: 8px; opacity: 0; transition: opacity 0.2s;
        }
        .tmpl-card:hover .tmpl-card-ov { opacity: 1; }
        .tmpl-card-title {
          font-size: 11px; font-weight: 700; color: #fff;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .tmpl-card-prompt {
          font-size: 9px; color: rgba(255,255,255,0.7);
          display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
          margin-top: 2px;
        }
      `}</style>
      
      <div className="tmpl-slide-overlay" onClick={onClose} />
      
      <div className="tmpl-slide-drawer">
        <div style={{ padding: '16px', borderBottom: '1px solid var(--b1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icons.Sparkle sz={16} color="var(--ac2)" />
            <span style={{ fontWeight: 800, fontSize: 14 }}>Model Templates</span>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--t2)', cursor: 'pointer', padding: 4 }}>
            <Icons.X sz={16} />
          </button>
        </div>
        
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
          {loading ? (
            <div style={{ color: 'var(--t3)', fontSize: 12, textAlign: 'center', marginTop: 20 }}>Loading templates...</div>
          ) : templates.length === 0 ? (
            <div style={{ color: 'var(--t3)', fontSize: 12, textAlign: 'center', marginTop: 20 }}>
              No templates available for this model yet.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {templates.map(tmpl => {
                let mediaSrc = tmpl.mediaUrl || tmpl.refImageUrl || tmpl.startImageUrl;
                if (Array.isArray(mediaSrc)) mediaSrc = mediaSrc[0];
                const isVideoMedia = mediaSrc && (mediaSrc.match(/\.(mp4|webm|ogg)$/i) || (tmpl.mediaUrl && tmpl.mediaUrl === mediaSrc && (tmpl.type === 'video' || tmpl.editType === 'text_to_video' || tmpl.editType === 'frame-to-video')));
                
                return (
                  <div key={tmpl.id || tmpl.$id} className="tmpl-card" onClick={() => onSelect(tmpl)}>
                    {mediaSrc ? (
                      isVideoMedia ? (
                        <video src={mediaSrc} autoPlay loop muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <img src={mediaSrc} alt={tmpl.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      )
                    ) : (
                      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--s3)' }}>
                        <Icons.ImgFrame sz={24} color="var(--t3)" />
                      </div>
                    )}
                    <div className="tmpl-card-ov">
                      <div className="tmpl-card-title">{tmpl.name || 'Untitled'}</div>
                      <div className="tmpl-card-prompt">{tmpl.prompt}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
